import { Link, useNavigate } from 'react-router-dom';
import './astyles/AdminDashboard.css';
import { useEffect, useState } from 'react';
import ProfileModal from '../../Components/Profile/ProfileModal';
import { fetchProjects } from '../../services/api';
import { doSignOut } from '../../Firebase/auth';
import { LogOut, Users, FolderKanban, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import ReviewsSection from './ReviewsSection';

interface ProjectStatusData {
  completed: number;
  active: number;
  unassigned: number;
  completedCount: number;
  activeCount: number;
  unassignedCount: number;
  total: number;
}

export default function AdminDashboard() {
  const [projectStatusData, setProjectStatusData] = useState<ProjectStatusData>({
    completed: 0,
    active: 0,
    unassigned: 0,
    completedCount: 0,
    activeCount: 0,
    unassignedCount: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await doSignOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        const projects = await fetchProjects('admin');
        
        const totalProjects = projects.length;
        if (totalProjects > 0) {
          const completed = projects.filter(p => p.status === 'Completed').length;
          const active = projects.filter(p => p.status === 'Active').length;
          const unassigned = projects.filter(p => p.status === 'Unassigned').length;

          setProjectStatusData({
            completed: Math.round((completed / totalProjects) * 100),
            active: Math.round((active / totalProjects) * 100),
            unassigned: Math.round((unassigned / totalProjects) * 100),
            completedCount: completed,
            activeCount: active,
            unassignedCount: unassigned,
            total: totalProjects
          });
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getUserInitials = () => {
    const userName = "Admin User";
    return userName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="dashboard-redesigned">
      {/* Header */}
      <header className="dashboard-header-redesigned">
        <div className="header-left">
          <h1 className="dashboard-title">Welcome Back, Admin! 👋</h1>
          <p className="dashboard-subtitle">Here's what's happening with your projects today</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleLogout}
            className="logout-button-redesigned"
            aria-label="Log out"
            title="Log Out"
          >
            <LogOut size={18} />
            <span className="logout-text">Logout</span>
          </button>
          <button 
            onClick={() => setIsProfileModalOpen(true)}
            className="profile-button-redesigned"
            aria-label="Open profile menu"
            title="My Profile"
          >
            {getUserInitials()}
          </button>
        </div>
      </header>

      <ProfileModal 
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Main Content */}
      <div className="dashboard-content-redesigned">
        {/* Stats Overview Cards */}
        <div className="stats-grid">
          <div className="stat-card stat-card-blue">
            <div className="stat-icon">
              <FolderKanban size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Projects</p>
              <h3 className="stat-value">{projectStatusData.total}</h3>
            </div>
          </div>

          <div className="stat-card stat-card-green">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Completed</p>
              <h3 className="stat-value">{projectStatusData.completedCount}</h3>
            </div>
          </div>

          <div className="stat-card stat-card-purple">
            <div className="stat-icon">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Active Projects</p>
              <h3 className="stat-value">{projectStatusData.activeCount}</h3>
            </div>
          </div>

          <div className="stat-card stat-card-orange">
            <div className="stat-icon">
              <AlertCircle size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Unassigned</p>
              <h3 className="stat-value">{projectStatusData.unassignedCount}</h3>
            </div>
          </div>
        </div>

        {/* Quick Access Tiles */}
        <div className="quick-access-section">
          <h2 className="section-title">Quick Access</h2>
          <div className="quick-access-grid">
            <Link to="/admin/manage-records/volunteers" className="access-card volunteers-card">
              <div className="card-icon-wrapper">
                <Users size={32} />
              </div>
              <div className="card-content">
                <h3>Volunteers</h3>
                <p>Manage all volunteers and their profiles</p>
              </div>
              <div className="card-arrow">→</div>
            </Link>
            
            <Link to="/admin/manage-records/projects" className="access-card projects-card">
              <div className="card-icon-wrapper">
                <FolderKanban size={32} />
              </div>
              <div className="card-content">
                <h3>Projects</h3>
                <p>View and manage all projects</p>
              </div>
              <div className="card-arrow">→</div>
            </Link>

            <Link to="/admin/task-approvals" className="access-card approvals-card">
              <div className="card-icon-wrapper">
                <CheckCircle size={32} />
              </div>
              <div className="card-content">
                <h3>Task Approvals</h3>
                <p>Review pending task applications</p>
              </div>
              <div className="card-arrow">→</div>
            </Link>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="dashboard-two-column">
          {/* Project Status Chart */}
          <div className="widget-card project-status-widget">
            <h3 className="widget-title">Project Status Overview</h3>
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading project data...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <AlertCircle size={24} />
                <p>Error: {error}</p>
              </div>
            ) : (
              <div className="pie-chart-wrapper">
                <div 
                  className="pie-chart-redesigned"
                  style={{ 
                    '--completed': `${projectStatusData.completed}%`,
                    '--active': `${projectStatusData.active}%`,
                    '--unassigned': `${projectStatusData.unassigned}%`
                  } as React.CSSProperties}
                >
                  <div className="pie-center">
                    <span className="pie-total">{projectStatusData.total}</span>
                    <span className="pie-label">Projects</span>
                  </div>
                </div>
                <div className="pie-legend-redesigned">
                  <div className="legend-item">
                    <span className="legend-dot completed-dot"></span>
                    <span className="legend-text">Completed</span>
                    <span className="legend-value">{projectStatusData.completed}%</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot active-dot"></span>
                    <span className="legend-text">Active</span>
                    <span className="legend-value">{projectStatusData.active}%</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot unassigned-dot"></span>
                    <span className="legend-text">Unassigned</span>
                    <span className="legend-value">{projectStatusData.unassigned}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Community Reviews */}
          <ReviewsSection />
        </div>
      </div>
    </div>
  );
}